import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, DatePicker, Form, Input, Row, Table, Tag, message } from 'antd';
import moment from 'moment';
import { isRootSessionUser, searchAuditTrailAPI } from 'services/system_setup/auditTrail';
import SearchPager from 'shared_components/search_pager';

const { RangePicker } = DatePicker;

const DEFAULT_PAGE_SIZE = 5;
const MODULE_LABEL_MAP = {
    inv: 'INVOICE',
    dr: 'DELIVERY',
    pymt: 'PAYMENT',
    sys: 'SYSTEM SETUP',
    cust: 'CUSTOMERS',
    agnt: 'AGENTS',
    rpt: 'REPORTS',
};
const ACTION_LABEL_MAP = {
    SAVE: 'SAVE',
    POST: 'POST',
    CANCEL: 'CANCEL',
    OVERRIDE: 'OVERRIDE',
    EDIT: 'UPDATE',
    UPDATE: 'UPDATE',
    DELETE: 'DELETE',
    SEARCH: 'SEARCH',
    VIEW: 'VIEW',
    LOAD: 'OPEN',
    PUSH: 'NAVIGATE',
    REPLACE: 'NAVIGATE',
};

function normalizeAuditResponse(response) {
    if (Array.isArray(response)) {
        return { rows: response, total: response.length };
    }

    if (response && Array.isArray(response.rows)) {
        return {
            rows: response.rows,
            total: Number(response.total || response.count || response.rows.length || 0),
        };
    }

    if (response && Array.isArray(response.data)) {
        return {
            rows: response.data,
            total: Number(response.total || response.count || response.data.length || 0),
        };
    }

    return { rows: [], total: 0 };
}

function createDefaultDateRange() {
    return [moment().subtract(6, 'days').startOf('day'), moment().endOf('day')];
}

function parseJSON(value) {
    if (!value || typeof value !== 'string') return null;
    try {
        return JSON.parse(value);
    } catch (error) {
        return null;
    }
}

function getActionCode(record) {
    const rawCode = String((record && record.activity_code) || '').trim().toUpperCase();
    if (!rawCode) return '';

    const token = rawCode.split('_').filter(Boolean)[0] || '';
    return token;
}

function getActionLabel(record) {
    const actionCode = getActionCode(record);
    return ACTION_LABEL_MAP[actionCode] || actionCode || 'ACTION';
}

function getModuleLabel(record) {
    const moduleCode = String((record && record.module_code) || '').toLowerCase();
    const moduleName = String((record && record.module_name) || '').trim();

    if (moduleName) return moduleName.toUpperCase();
    return MODULE_LABEL_MAP[moduleCode] || moduleCode.toUpperCase() || 'UNKNOWN';
}

function getTransactionType(record, payload) {
    const payloadType = String((payload && payload.transaction_type) || '').trim();
    if (payloadType) return payloadType.toUpperCase();

    const subModuleCode = String((record && record.sub_module_code) || '').toLowerCase();
    if (subModuleCode.includes('override')) return 'OVERRIDE';

    const moduleCode = String((record && record.module_code) || '').toLowerCase();
    if (moduleCode === 'inv') return 'INVOICE';
    if (moduleCode === 'dr') return 'DELIVERY';
    if (moduleCode === 'pymt') return 'PAYMENT';
    if (moduleCode === 'sys') return 'SYSTEM';
    return moduleCode.toUpperCase() || 'GENERAL';
}

function getReferenceNo(payload) {
    if (!payload || typeof payload !== 'object') return '';

    const reference = payload.transaction_no
        || payload.invoice_no
        || payload.dr_no
        || payload.receipt_no
        || payload.or_no
        || payload.invoiceNumber
        || payload.DrNo
        || '';

    return String(reference || '').trim();
}

function asNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(String(value).replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
}

function formatAmount(value) {
    const parsed = asNumber(value);
    if (parsed === null) return '';
    return parsed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function truncateText(value, maxLength = 160) {
    const text = String(value || '');
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
}

function buildDetails(record, payload, actionLabel, referenceNo) {
    const fallbackDescription = String((record && record.description) || '').trim();
    const previousStatus = String((payload && payload.previous_status) || '').trim().toUpperCase();
    const targetStatus = String((payload && payload.target_status) || '').trim().toUpperCase();
    const detailCount = Number((payload && payload.detail_count) || 0);
    const amount = formatAmount(
        (payload && payload.total_amount_due)
        || (payload && payload.total_amount)
        || (payload && payload.total_payment_amount),
    );

    const detailParts = [];

    if (previousStatus || targetStatus) {
        detailParts.push(`STATUS ${previousStatus || 'UNKNOWN'} -> ${targetStatus || 'UNKNOWN'}`);
    }
    if (amount) {
        detailParts.push(`AMOUNT ${amount}`);
    }
    if (detailCount > 0) {
        detailParts.push(`LINES ${detailCount}`);
    }
    if (referenceNo) {
        detailParts.push(`REF ${referenceNo}`);
    }

    if (detailParts.length > 0) {
        if (actionLabel === 'OVERRIDE') {
            return detailParts.join(' | ');
        }

        return `${fallbackDescription || actionLabel} | ${detailParts.join(' | ')}`;
    }

    return fallbackDescription || actionLabel;
}

function mapAuditRow(row) {
    const payload = parseJSON(row && row.parameters);
    const actionLabel = getActionLabel(row);
    const referenceNo = getReferenceNo(payload);

    return {
        ...row,
        ui_date_time: row.formatted_created_date || row.formatted_date || row.created_date || '',
        ui_user: row.created_by_name || row.user_name || row.created_by || '',
        ui_module: getModuleLabel(row),
        ui_transaction_type: getTransactionType(row, payload),
        ui_reference_no: referenceNo || '-',
        ui_action: actionLabel,
        ui_status: row.is_success === 1 || row.is_success === true ? 'SUCCESS' : 'FAILED',
        ui_details: buildDetails(row, payload, actionLabel, referenceNo),
    };
}

function buildSearchPayload(formValues = {}, pagination = {}) {
    const values = formValues || {};
    const dateRange = values.dateRange || [];
    const dateFrom = dateRange[0] ? dateRange[0].clone().startOf('day').format('YYYY-MM-DD HH:mm:ss') : '';
    const dateTo = dateRange[1] ? dateRange[1].clone().endOf('day').format('YYYY-MM-DD HH:mm:ss') : '';
    const keyword = values.keyword ? String(values.keyword).trim() : '';

    return {
        dateFrom,
        dateTo,
        keyword,
        page: pagination.current || 1,
        pageSize: pagination.pageSize || DEFAULT_PAGE_SIZE,
    };
}

const tableColumns = [
    {
        title: 'DATE/TIME',
        dataIndex: 'ui_date_time',
        key: 'ui_date_time',
        width: 170,
    },
    {
        title: 'USER',
        dataIndex: 'ui_user',
        key: 'ui_user',
        width: 150,
    },
    {
        title: 'MODULE',
        dataIndex: 'ui_module',
        key: 'ui_module',
        width: 160,
    },
    {
        title: 'TRANSACTION TYPE',
        dataIndex: 'ui_transaction_type',
        key: 'ui_transaction_type',
        width: 170,
    },
    {
        title: 'REFERENCE NO',
        dataIndex: 'ui_reference_no',
        key: 'ui_reference_no',
        width: 170,
    },
    {
        title: 'ACTION',
        dataIndex: 'ui_action',
        key: 'ui_action',
        width: 120,
    },
    {
        title: 'DETAILS',
        dataIndex: 'ui_details',
        key: 'ui_details',
        width: 520,
        render: (value) => <span title={value}>{truncateText(value, 220)}</span>,
    },
    {
        title: 'STATUS',
        dataIndex: 'ui_status',
        key: 'ui_status',
        width: 100,
        render: (value) => (value === 'SUCCESS' ? <Tag color="green">SUCCESS</Tag> : <Tag color="red">FAILED</Tag>),
    },
];

export default function AuditTrailTab() {
    const [form] = Form.useForm();
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [pagination, setPagination] = useState({
        current: 1,
        total: 0,
    });
    const [activeFilters, setActiveFilters] = useState({});
    const isRoot = isRootSessionUser();

    const fetchAuditTrail = async (filters = activeFilters, pageState = {}, showEmptyMessage = false) => {
        setLoading(true);
        try {
            const currentPage = pageState.current || pagination.current || 1;
            const effectivePageSize = pageState.pageSize || pageSize || DEFAULT_PAGE_SIZE;
            const payload = buildSearchPayload(filters, pageState);
            payload.page = currentPage;
            payload.pageSize = effectivePageSize;
            const response = await searchAuditTrailAPI(payload);
            const normalized = normalizeAuditResponse(response);
            const mappedRows = normalized.rows.map(mapAuditRow);

            setTableData(mappedRows);
            setPagination((prev) => ({
                ...prev,
                current: currentPage,
                total: normalized.total,
            }));
            setActiveFilters(filters);

            if (showEmptyMessage && mappedRows.length === 0) {
                message.info('No audit trail records found');
            }
        } catch (error) {
            console.log('fetchAuditTrail error:', error);
            message.error('Failed to fetch audit trail data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isRoot) return;

        const defaultValues = {
            dateRange: createDefaultDateRange(),
            keyword: '',
        };

        form.setFieldsValue(defaultValues);
        setPageSize(DEFAULT_PAGE_SIZE);
        fetchAuditTrail(defaultValues, { current: 1, pageSize: DEFAULT_PAGE_SIZE }, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRoot]);

    const onSearch = async () => {
        const values = await form.validateFields();
        fetchAuditTrail(values, { current: 1, pageSize }, true);
    };

    const onClear = () => {
        const defaultValues = {
            dateRange: createDefaultDateRange(),
            keyword: '',
        };

        form.setFieldsValue(defaultValues);
        setPageSize(DEFAULT_PAGE_SIZE);
        fetchAuditTrail(defaultValues, { current: 1, pageSize: DEFAULT_PAGE_SIZE }, false);
    };

    const onTableChange = (pager) => {
        fetchAuditTrail(activeFilters, { current: pager.current, pageSize }, false);
    };

    const handleChangePageSize = (pageSize) => {
        const nextPageSize = Number(pageSize) || DEFAULT_PAGE_SIZE;
        setPageSize(nextPageSize);
        fetchAuditTrail(activeFilters, { current: 1, pageSize: nextPageSize }, false);
    };

    if (!isRoot) {
        return (
            <Alert
                message="Unauthorized"
                description="Only ROOT users can access Audit Trail."
                type="error"
                showIcon
            />
        );
    }

    return (
        <div>
            <Form
                form={form}
                layout="vertical"
                onFinish={onSearch}
                style={{ marginBottom: 8 }}
            >
                <Row gutter={[8, 1]} align="bottom">
                    <Col xs={24} sm={12} md={10} lg={8} xl={6}>
                        <Form.Item label="DATE RANGE" name="dateRange">
                            <RangePicker
                                style={{ width: '100%' }}
                                format="MM-DD-YYYY"
                                allowClear={false}
                                disabledDate={(current) => current && current > moment().endOf('day')}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={14} lg={10} xl={10}>
                        <Form.Item label="SEARCH" name="keyword">
                            <Input
                                placeholder="Search user, module, transaction no, action, or details"
                                maxLength={200}
                                allowClear
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={24} lg={6} xl={8} style={{ textAlign: 'right' }}>
                        <Form.Item label=" " colon={false}>
                            <Button onClick={onClear} style={{ marginRight: 8 }}>
                                CLEAR
                            </Button>
                            <Button type="primary" htmlType="submit">
                                SEARCH
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            <SearchPager
                pageTotal={pagination.total || 0}
                pageSize={pageSize || DEFAULT_PAGE_SIZE}
                handleChangeSize={handleChangePageSize}
            />

            <Table
                rowKey={(record, index) => record.audit_trail_id || `${record.activity_code}_${index}`}
                dataSource={tableData}
                columns={tableColumns}
                bordered
                loading={loading}
                pagination={{ ...pagination, pageSize, showSizeChanger: false }}
                onChange={onTableChange}
                size="small"
                scroll={{ x: 1500, y: 440 }}
            />
        </div>
    );
}
